import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { IMAGES } from "../mock";
import { postsApi, categoriesApi } from "../api";
import { Reveal, PageHero, NewsCard } from "../components/Shared";
import Seo from "../components/Seo";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const activeCategory = searchParams.get("category") || "All";

  useEffect(() => {
    Promise.all([postsApi.list(), categoriesApi.list()])
      .then(([p, c]) => {
        setPosts(p);
        setCategories(c);
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = posts;
    if (activeCategory !== "All") list = list.filter((p) => p.category === activeCategory);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          (p.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [posts, activeCategory, search]);

  const setCategory = (cat) => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (search.trim()) params.q = search.trim();
    setSearchParams(params);
  };

  return (
    <div data-testid="blog-page">
      <Seo
        title="Latest News"
        description="Race reports, bloodstock updates, team news and insights from Global Equine Group — keep up to date with our winners and news from the farm."
        image={IMAGES.racing3}
      />
      <PageHero image={IMAGES.racing3} kicker="Keep Up To Date" title="Latest News">
        <p className="text-[#b9c9d6] mt-6 max-w-2xl text-lg leading-relaxed">
          Race reports, bloodstock updates and insights from the Global Equine Group team.
        </p>
      </PageHero>

      <section className="py-20 bg-white min-h-[50vh]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {/* Filters */}
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-14">
              <div className="flex flex-wrap items-center gap-2" data-testid="category-filters">
                {["All", ...categories.map((c) => c.name)].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-5 py-2 text-sm font-medium tracking-wide transition-all duration-300 border
                      ${activeCategory === cat
                        ? "bg-[#0f2438] text-white border-[#0f2438]"
                        : "bg-white text-[#5b6d7d] border-[#d7e3ec] hover:border-[#29abe2] hover:text-[#29abe2]"}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="relative w-full lg:w-72">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8aa0b0]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search news..."
                  data-testid="blog-search-input"
                  className="w-full pl-11 pr-4 py-3 text-sm border border-[#d7e3ec] outline-none focus:border-[#29abe2] transition-colors"
                />
              </div>
            </div>
          </Reveal>

          {/* Grid */}
          {filtered.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {filtered.map((post, i) => (
                <NewsCard key={post.id} post={post} delay={i % 3} />
              ))}
            </div>
          ) : (
            <div className="text-center py-24" data-testid="blog-empty-state">
              <p className="font-serif-d text-2xl text-[#0f2438] mb-2">No articles found</p>
              <p className="text-[#8aa0b0]">Try a different search or category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
